import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
} from "@mui/material";
import {
  Home,
  Inventory,
  Storefront,
  ExpandLess,
  ExpandMore,
  People,
  Description,
  LocalOffer,
  AccountBalance,
  AccountTree,
} from "@mui/icons-material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const SidebarMenu = ({ open, submenuOpen, setSubmenuOpen, navigate }) => {
  const drawerRef = useRef(null);
  const [accountingOpen, setAccountingOpen] = useState(false);

  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isAdmin = localStorage.getItem("is_admin") === "true";
  const canSeeAccounting = isSuperuser || isAdmin;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setSubmenuOpen(false);
        setAccountingOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setSubmenuOpen]);

  const renderText = (label) =>
    open ? (
      <ListItemText
        primary={label}
        sx={{
          ml: 1.6,
          color: "#fff",
          whiteSpace: "nowrap",
          "& .MuiListItemText-primary": { fontSize: "0.92rem", fontWeight: 500 },
        }}
      />
    ) : (
      <Box
        sx={{
          fontSize: "0.62rem",
          textAlign: "center",
          color: "#fff",
          width: "100%",
          mt: 0.45,
          mb: 0.25,
          lineHeight: 1.15,
        }}
      >
        {label}
      </Box>
    );

  return (
    <Drawer
      ref={drawerRef}
      variant="permanent"
      sx={{
        width: open ? 240 : 85,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? 240 : 85,
          boxSizing: "border-box",
          transition: "width 0.3s",
          bgcolor: "#1565C0",
          color: "#fff",
          overflowX: "hidden",
          display: "flex",
          justifyContent: "flex-start",
        },
      }}
    >
      <List sx={{ width: "100%", py: 1 }}>
        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => navigate("/admin-panel")}
            sx={{
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <Home sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Inicio")}
          </ListItemButton>
        </ListItem>

        {canSeeAccounting && (
          <>
            <ListItem disablePadding sx={{ justifyContent: "center" }}>
              <ListItemButton
                onClick={() => setAccountingOpen(!accountingOpen)}
                sx={{
                  bgcolor: accountingOpen ? "#0d47a1" : "inherit",
                  "&:hover": { bgcolor: "#0d47a1" },
                  flexDirection: open ? "row" : "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: open ? 0.85 : 0.75,
                }}
              >
                <AccountBalance sx={{ color: "#fff", fontSize: 23 }} />
                {renderText("Contabilidad")}
                {open &&
                  (accountingOpen ? (
                    <ExpandLess sx={{ color: "#fff", ml: "auto" }} />
                  ) : (
                    <ExpandMore sx={{ color: "#fff", ml: "auto" }} />
                  ))}
              </ListItemButton>
            </ListItem>

            <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 0 }}>
                <ListItemButton
                  onClick={() => navigate("/admin-panel/admin-cuentas")}
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    py: 0.7,
                    px: open ? 2 : 1.2,
                    bgcolor: "#0d47a1",
                    "&:hover": { bgcolor: "#0b3c8a" },
                  }}
                >
                  {renderText("Plan de Cuentas")}
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => navigate("/admin-panel/secciones")}
            sx={{
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <Inventory sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Secciones")}
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => setSubmenuOpen(!submenuOpen)}
            sx={{
              bgcolor: submenuOpen ? "#0d47a1" : "inherit",
              "&:hover": { bgcolor: "#0d47a1" },
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <Storefront sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Inventario")}
            {open &&
              (submenuOpen ? (
                <ExpandLess sx={{ color: "#fff", ml: "auto" }} />
              ) : (
                <ExpandMore sx={{ color: "#fff", ml: "auto" }} />
              ))}
          </ListItemButton>
        </ListItem>

        <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 0 }}>
            <ListItemButton
              onClick={() => navigate("/admin-panel/inventario/productos")}
              sx={{
                py: 0.7,
                px: open ? 2 : 1.2,
                bgcolor: "#0d47a1",
                "&:hover": { bgcolor: "#0b3c8a" },
              }}
            >
              {renderText("Productos")}
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate("/admin-panel/inventario/movimientos")}
              sx={{
                py: 0.7,
                px: open ? 2 : 1.2,
                bgcolor: "#0d47a1",
                "&:hover": { bgcolor: "#0b3c8a" },
              }}
            >
              {renderText("Movimientos")}
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate("/admin-panel/inventario/variantes")}
              sx={{
                py: 0.7,
                px: open ? 2 : 1.2,
                bgcolor: "#0d47a1",
                "&:hover": { bgcolor: "#0b3c8a" },
              }}
            >
              {renderText("Licencias")}
            </ListItemButton>

            <ListItemButton
              onClick={() => navigate("/admin-panel/servicios")}
              sx={{
                py: 0.7,
                px: open ? 2 : 1.2,
                bgcolor: "#0d47a1",
                "&:hover": { bgcolor: "#0b3c8a" },
              }}
            >
              {renderText("Servicios")}
            </ListItemButton>
          </List>
        </Collapse>

        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => navigate("/admin-panel/proformas")}
            sx={{
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <Description sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Proformas")}
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => navigate("/admin-panel/clientes")}
            sx={{
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <People sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Personas")}
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ justifyContent: "center" }}>
          <ListItemButton
            onClick={() => navigate("/admin-panel/promociones")}
            sx={{
              flexDirection: open ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              py: open ? 0.85 : 0.75,
            }}
          >
            <LocalOffer sx={{ color: "#fff", fontSize: 23 }} />
            {renderText("Promociones")}
          </ListItemButton>
        </ListItem>

        {canSeeAccounting && (
          <ListItem disablePadding sx={{ justifyContent: "center" }}>
            <ListItemButton
              onClick={() => navigate("/admin-panel/bancos")}
              sx={{
                flexDirection: open ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                py: open ? 0.85 : 0.75,
              }}
            >
              <AttachMoneyIcon sx={{ color: "#fff", fontSize: 23 }} />
              {renderText("Bancos")}
            </ListItemButton>
          </ListItem>
        )}

      </List>
    </Drawer>
  );
};

export default SidebarMenu;
